import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Alert, Button, TextField } from "@mui/material";
import { toast } from "sonner";
import { useAuth } from "@/modules/auth";
import {
  useChangePassword,
  useRequestOtp,
  useResetPassword,
  useVerifyOtp,
} from "@/api/hooks/auth";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = "current" | "otp_request" | "otp_verify" | "otp_set";

export function ChangePasswordDialog({ open, onClose }: Props) {
  const { user } = useAuth();
  const [mode, setMode] = useState<Mode>("current");

  // current-password mode state
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  // otp mode state
  const [otpCode, setOtpCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [otpDestination, setOtpDestination] = useState("");
  const [otpChannel, setOtpChannel] = useState<"email" | "whatsapp" | null>(null);

  const change = useChangePassword();
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const resetPw = useResetPassword();

  useEffect(() => {
    if (!open) return;
    setMode("current");
    setCurrent("");
    setNext("");
    setConfirm("");
    setOtpCode("");
    setResetToken("");
    setOtpDestination("");
    setOtpChannel(null);
  }, [open]);

  const close = () => {
    if (change.isPending || requestOtp.isPending || verifyOtp.isPending || resetPw.isPending) {
      return;
    }
    onClose();
  };

  // ---------- current-password submit ----------
  const validCurrent =
    current.length > 0 && next.length >= 8 && next === confirm && next !== current;

  const submitCurrent = async () => {
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      toast.success("Password changed");
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e?.response?.data?.detail || "Failed to change password");
    }
  };

  // ---------- otp request ----------
  const sendOtp = async (channel?: "email" | "whatsapp") => {
    if (!user?.email) return;
    try {
      const r = await requestOtp.mutateAsync({ email: user.email, channel: channel ?? null });
      setOtpDestination(r.destinationMasked);
      setOtpChannel(r.channel);
      setMode("otp_verify");
      toast.success(`Code sent to ${r.destinationMasked} via ${r.channel}`);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e?.response?.data?.detail || "Failed to send code");
    }
  };

  // ---------- otp verify ----------
  const submitOtpVerify = async () => {
    if (!user?.email || otpCode.length !== 6) return;
    try {
      const r = await verifyOtp.mutateAsync({ email: user.email, code: otpCode });
      setResetToken(r.token);
      setMode("otp_set");
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
  };

  // ---------- otp set new password ----------
  const validNewSet = next.length >= 8 && next === confirm;

  const submitOtpSet = async () => {
    try {
      await resetPw.mutateAsync({ token: resetToken, newPassword: next });
      toast.success("Password reset. Sign back in if asked.");
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="wm-modal-overlay" onClick={close}>
      <div className="wm-modal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="wm-modal-header">
          <div className="wm-modal-icon">
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="7" width="10" height="7" rx="1.5" />
              <path d="M5.5 7V4.5a2.5 2.5 0 0 1 5 0V7" strokeLinecap="round" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="wm-modal-title">
              {mode === "current" && "Change password"}
              {mode === "otp_request" && "Reset password via OTP"}
              {mode === "otp_verify" && "Enter OTP"}
              {mode === "otp_set" && "Set new password"}
            </div>
            <div className="wm-modal-sub">
              {mode === "current" && "Verify with current password to change."}
              {mode === "otp_request" && `Send a 6-digit code to ${user?.email ?? "your account"}.`}
              {mode === "otp_verify" && `Sent to ${otpDestination} (${otpChannel}). Expires in 10 min.`}
              {mode === "otp_set" && "Code verified — choose a new password."}
            </div>
          </div>
          <button type="button" className="wm-modal-x" onClick={close} aria-label="Close">×</button>
        </div>

        <div className="wm-modal-body">
          {/* CURRENT-PASSWORD MODE */}
          {mode === "current" && (
            <>
              <TextField
                label="Current password"
                type="password"
                size="small"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                fullWidth
                autoFocus
              />
              <TextField
                label="New password"
                type="password"
                size="small"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                fullWidth
                helperText="At least 8 characters. Must differ from current."
                error={next.length > 0 && next.length < 8}
              />
              <TextField
                label="Confirm new password"
                type="password"
                size="small"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                fullWidth
                error={confirm.length > 0 && confirm !== next}
                helperText={confirm.length > 0 && confirm !== next ? "Passwords do not match" : " "}
              />
              {next.length > 0 && next === current ? (
                <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                  New password must differ from current password.
                </Alert>
              ) : null}
              <div style={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="wm-btn sm"
                  onClick={() => setMode("otp_request")}
                  style={{ color: "var(--wm-accent)" }}
                >
                  Forgot current password?
                </button>
              </div>
            </>
          )}

          {/* OTP REQUEST */}
          {mode === "otp_request" && (
            <>
              <Alert severity="info" sx={{ fontSize: 12.5 }}>
                We'll send a 6-digit code to your email. If you've added a phone number,
                you can choose WhatsApp instead.
              </Alert>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => sendOtp("email")}
                  disabled={requestOtp.isPending}
                >
                  {requestOtp.isPending ? "Sending…" : "Send via Email"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => sendOtp("whatsapp")}
                  disabled={requestOtp.isPending}
                >
                  Send via WhatsApp
                </Button>
              </div>
            </>
          )}

          {/* OTP VERIFY */}
          {mode === "otp_verify" && (
            <>
              <TextField
                label="6-digit code"
                size="small"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                fullWidth
                autoFocus
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 6,
                  style: { letterSpacing: 6, fontFamily: "monospace", fontSize: 20, textAlign: "center" },
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <button type="button" className="wm-btn sm" onClick={() => setMode("otp_request")}>
                  ← Send again
                </button>
              </div>
            </>
          )}

          {/* OTP SET NEW PW */}
          {mode === "otp_set" && (
            <>
              <TextField
                label="New password"
                type="password"
                size="small"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                fullWidth
                autoFocus
                helperText="At least 8 characters."
                error={next.length > 0 && next.length < 8}
              />
              <TextField
                label="Confirm new password"
                type="password"
                size="small"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                fullWidth
                error={confirm.length > 0 && confirm !== next}
              />
            </>
          )}
        </div>

        <div className="wm-modal-footer">
          <Button onClick={close} size="small" sx={{ ml: "auto" }} disabled={change.isPending || resetPw.isPending}>
            Cancel
          </Button>
          {mode === "current" && (
            <Button
              variant="contained"
              size="small"
              onClick={submitCurrent}
              disabled={!validCurrent || change.isPending}
            >
              {change.isPending ? "Changing…" : "Change password"}
            </Button>
          )}
          {mode === "otp_verify" && (
            <Button
              variant="contained"
              size="small"
              onClick={submitOtpVerify}
              disabled={otpCode.length !== 6 || verifyOtp.isPending}
            >
              {verifyOtp.isPending ? "Verifying…" : "Verify"}
            </Button>
          )}
          {mode === "otp_set" && (
            <Button
              variant="contained"
              size="small"
              onClick={submitOtpSet}
              disabled={!validNewSet || resetPw.isPending}
            >
              {resetPw.isPending ? "Setting…" : "Set new password"}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
